import { from } from "rxjs";
import { APIResource, APIMethod } from "libs/utils/http/decorator/UrlDecorator";
import { apiMethodContext } from "libs/utils/http/builder/UrlConfig";
import { CloudConsoleClient } from "libs/utils/http/client/HttpClient";
import { INoAuthResponse } from "../components/NoAuthConfiguration";
import "reflect-metadata";

export type IdentityApplicationEnvironmentType =
  | "trimble-pre-prod"
  | "trimble-prod";

export interface IdentityApplicationPublishedGateway {
  consumerKey: string;
  consumerSecret: string[];
  grantTypes?: string[];
  logoutUri?: string[];
  organisationName: IdentityApplicationEnvironmentType;
  redirectUri?: string[];
  enforceScopeValidation?: boolean;
}



export interface IdentityApplicationSPMetadata {
  assertionConsumerUrl: string;
  x509RequestSigningCert?: string;
}

export interface IdentityApplicationType {
  id: string;
  name: IdentityApplicationTypes;
}

export interface ApiService{
  basePath: string;
}

export interface IdentityApplication {
  applicationId: string;
  applicationName: string;
  applicationType?: IdentityApplicationType;
  displayName?: string;
  description?: string;
  organisationName: IdentityApplicationEnvironmentType;
  applicationGatewayResponses?: IdentityApplicationPublishedGateway[];
  samlSpMetadata?: IdentityApplicationSPMetadata;
  userId?: string;
  isOwnedByUser?: Boolean;
  createdBy?: string;
  consumerKey: string;
  created?: string | Date;
  service?: ApiService; 
}

export interface IdentityApplicationCreateRequest {
  name?: string;
  displayName?: string;
  description?: string;
  applicationType: IdentityApplicationTypes;
  basePath?: string;
  grantTypes?: string[];
  redirectUri?: string[];
  logoutUri?: string[];
}

export interface IdentityApplicationUpdateRequest {
  name: string;
  displayName?: string;
  description?: string;
}

export interface IdentityApplicationPublishRequest {
  grantTypes?: string[];
  redirectUri?: string[];
  logoutUri?: string[];
  enforceScopeValidation?: true;
}

export interface IVersionDeployment {
  specFile: string;
  specId: string;
  deployments: IVersionDeploymentResponse[]; 
}

export interface IApplicationThrottling {
  consumerKey: string;
  applicationName: string;
  applicationType: string;
  teamName: string;
  accountName: string;
  transactionLimit: number;
  timeUnit: "ps" | "pm" | string; // Use string unions if units are known
}

/**
 * Default API throttling row for a deployment (publisher `DeploymentResponse.apiThrottling`).
 * Serialized from `deployment_throttling` for the `/*` + `*` resource.
 */
export interface IDeploymentApiThrottling {
  id?: string;
  spikeArrestEnabled?: boolean;
  spikeArrestLimit?: number;
  /** e.g. SECOND, MINUTE */
  spikeArrestTimeunit?: string;
  subscriptionQuotaEnabled?: boolean;
  subscriptionQuotaLimit?: number;
  subscriptionQuotaInterval?: number;
  /** e.g. MINUTE, HOUR */
  subscriptionQuotaTimeunit?: string;
}

export interface IVersionDeploymentResponse {
  deploymentId?: string;
  backendUrl?: string;
  deployedUrl?: string;
  timeout?: number;
  deployStatus?: string;
  provider?: string;
  type?: string;
  location?: string;
  /** Target region (e.g. "us-east-1") from deployment config */
  targetRegion?: string;
  /** Target cloud for the deployment (e.g. AWS, AZURE); may mirror {@link provider} */
  targetProvider?: string;
  /** Gateway region used for API gateway display */
  gatewayRegion?: string;
  apiThrottling?: IDeploymentApiThrottling | null;
  noAuth?: INoAuthResponse | null;
  applicationThrottling?: IApplicationThrottling[] | null;
}

export interface IVersionSpecResponse {
  specFile?: string;
  specId?: string;
  specUrl?: string;
  lintingResultsUri?: string | null;
  qualitativeResultsUri?: string | null;
  lintingStatus?: string | null;
  apiStandardVersion?: string;
  documentationUrl?: string;
}

export interface ApiStandardVersion {
  id: string;
  version: string;
  status: string;
  documentationUrl: string;
  created: string;
  updated: string;
}
//NOTE: only two application types are supported for now (application, resource-server)
export type IdentityApplicationTypes =
  | "application"
  | "APPLICATION"
  | "RESOURCE_SERVER"
  | "resource_server";

const formatError = (error: any, defaultMessage: string) =>
  error?.response?.data?.code !== undefined && error.response.data.message
    ? error.response.data.message
    : defaultMessage;

/**
 * Message from publisher API errors when `useAsyncAction` stores `error.response` on failure.
 */
export function formatPublisherHttpError(
  error: unknown,
  defaultMessage: string
): string {
  if (error && typeof error === "object") {
    const r = error as { data?: { message?: string } };
    if (r.data?.message) {
      return String(r.data.message);
    }
  }
  return defaultMessage;
}
export type IdentityApplicationPublishType = "publish" | "update";

/** deployV2 request body: provider, backendUrl, targetRegion, timeout */
export interface DeployV2Request {
  provider: "AWS" | "AZURE";
  backendUrl: string;
  targetRegion: string;
  timeout: number;
}

@APIResource(CloudConsoleClient, "publisher/v2/applications")
class ApiServiceFactory {
  @APIMethod("", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createApi(
    teamId: Record<string, string>,
    request?: IdentityApplicationCreateRequest
  ) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), request, {
        params: {
          ...teamId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey", {
    options: {
      noTrailingSlash: true,
    },
  })
  public delete(
   consumerKey: string
  ) {
    return from(
    CloudConsoleClient.delete<any>(apiMethodContext(arguments),{
        params: {
          consumerKey
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getAllApplication(gatewayName: string, applicationType: string) {
    return from(
      CloudConsoleClient.get<IdentityApplication[]>(
        apiMethodContext(arguments),
        {
          params: {
            gatewayName,
            includeEmailAddress: true,
            type: applicationType,
          },
        }
      ).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createNewVersion(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.post<any>(
        apiMethodContext(arguments),
        { version: "v" + version },
        {
          params: {
            consumerKey,
          },
        }
      ).then((value) => value.data)
    );
  }

  /** Legacy create (develop): POST .../deploy */
  @APIMethod("/:resourceAppId/versions/:version/deploy", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createVersionDeploymentLegacy(
    resourceAppId: string,
    version: string,
    targetUrl: string,
    region: string,
    timeout: number
  ) {
    const isFedRamp = process.env.REACT_APP_IS_FEDRAMP === "true";
    const provider = isFedRamp ? "AWS" : "AZURE";
    const locationType = isFedRamp
      ? "REGION"
      : region === "global"
        ? "GLOBAL"
        : "REGION";
    const location = isFedRamp ? "us-west-2" : region;

    return from(
      CloudConsoleClient.post<any>(
        apiMethodContext(arguments),
        {
          backendUrl: targetUrl,
          location,
          type: locationType,
          timeout,
          provider,
        },
        {
          params: {
            resourceAppId,
            version,
          },
        }
      ).then((value) => value.data)
    );
  }

  @APIMethod("/:resourceAppId/versions/:version/deployV2", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createVersionDeploymentV2(
    resourceAppId: string,
    version: string,
    request: DeployV2Request
  ) {
    const body: DeployV2Request = {
      provider: request.provider,
      backendUrl: request.backendUrl,
      targetRegion: request.targetRegion,
      timeout: request.timeout,
    };

    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), body, {
        params: {
          resourceAppId,
          version,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version/:version/subscriptions", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getSubscriptionsForVersion(consumerKey: string, version: string) {
    return from(CloudConsoleClient.get<IVersionDeploymentResponse[]>(
        apiMethodContext(arguments),
        {
          params: {
            consumerKey,
            version,
          },
        }
      ).then((value) => value.data));
  }


  @APIMethod("/:consumerKey/version/:version", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getVersionDeployment(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.get<IVersionDeploymentResponse[]>(
        apiMethodContext(arguments),
        {
          params: {
            consumerKey,
            version,
          },
        }
      ).then((value) => value.data)
    );
  }

    @APIMethod("/:consumerKey/version/:version/spec", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getVersionSpec(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.get<IVersionSpecResponse>(
        apiMethodContext(arguments),
        {
          params: {
            consumerKey,
            version,
          },
        }
      ).then((value) => value.data)
    );
  }

  @APIMethod("/deployment/:deploymentId/noauth", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createNoAuthConfiguration(deploymentId: string, request: any) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), request, {
        params: {
          deploymentId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/deployment/:deploymentId/noauth/:noAuthId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteNoAuthConfiguration(deploymentId: string, noAuthId: any) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          deploymentId,
          noAuthId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/deployment/:deploymentId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteDeployment(deploymentId?: string) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          deploymentId,
        },
      }).then((value) => value.data)
    );
  }

  /** Legacy update (develop): PUT .../deployment/{id} (same path as delete, HTTP PUT) */
  @APIMethod("/deployment/:deploymentId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public updateDeployment(
    request: { backendUrl: string; timeout: number },
    deploymentId?: string
  ) {
    return from(
      CloudConsoleClient.put<any>(apiMethodContext(arguments), request, {
        params: {
          deploymentId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/deployment/:deploymentId/updateV2", {
    options: {
      noTrailingSlash: true,
    },
  })
  public updateDeploymentV2(request: { backendUrl: string; timeout: number }, deploymentId?: string) {
    return from(
      CloudConsoleClient.put<any>(apiMethodContext(arguments), request, {
        params: {
          deploymentId,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version/:version", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteVersion(consumerKey: string, version: string) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          consumerKey,
          version,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/:consumerKey/version/:version/spec", {
    options: {
      noTrailingSlash: true,
    },
  })
  public uploadSpec(consumerKey: string, version: string, payload: any) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), payload, {
        params: {
          consumerKey,
          version,
        },
      }).then((value) => value.data)
    );
  }

  @APIMethod("/apiStandardVersions", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getApiStandardVersions() {
    return from(
      CloudConsoleClient.get<ApiStandardVersion[]>(apiMethodContext(arguments)).then(
        (value) => value.data
      )
    );
  }


    @APIMethod("/:consumerKey/version/:version/spec/:specId", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteSpec(consumerKey: string, version: string, specId: string | null) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments),{
        params: {
          consumerKey,
          version,
          specId
        },
      }).then((value) => value.data)
    );
  }

      @APIMethod("/:consumerKey/scopes")
      public addScope(consumerKey: string, scope: string, consentMessage: string, developerDocumentation: string, alwaysShowConsent: boolean) {
        return from(
          CloudConsoleClient.put<any>(apiMethodContext(arguments),{
            "scope": scope,
            "consentMessage": consentMessage,
            "developerDocumentation": developerDocumentation,
            "alwaysShowConsent": alwaysShowConsent
          },{
            params: {
              consumerKey
            },
          }).then((value) => value.data)
        );
      }
  
      @APIMethod("/:consumerKey/scopes",  {
          options: {
              noTrailingSlash: true
          }
      })
      public getScopes(consumerKey: string) {
        return from(
          CloudConsoleClient.get<any>(apiMethodContext(arguments), {
            params: {
              consumerKey          },
          }).then((value) => value.data)
        );
      }

    @APIMethod("/:consumerKey/scopes/update")
    public updateScope(consumerKey: string, scope: string, consentMessage: string, developerDocumentation: string, alwaysShowConsent: boolean) {
      return from(
        CloudConsoleClient.put<any>(apiMethodContext(arguments),{
          "scope": scope,
          "consentMessage": consentMessage,
          "developerDocumentation": developerDocumentation,
          "alwaysShowConsent": alwaysShowConsent
        },{
          params: {
            consumerKey
          },
        }).then((value) => value.data)
      );
    }

}

/** Cloud provider for GET /v2/apis/regions */
export type RegionProvider = "AWS" | "AZURE";

/** Response item from GET /v2/apis/regions */
export interface RegionItem {
  geography: string;
  targetRegion: string;
  targetProvider?: string;
  gatewayProvider?: string;
  /** When set, only one deployment per group is allowed; UI greys out siblings if any is deployed */
  regionalGroup?: string | null;
  /** Maps to regional gateway host keys (e.g. us-e → us-east.*.api.trimblecloud.com); prefer over regionalGroup for URLs */
  basePathIdentifier?: string | null;
}

@APIResource(CloudConsoleClient, "publisher/v2/apis")
class ApiRegionsServiceFactory {
  @APIMethod("/regions", { options: { noTrailingSlash: true } })
  public getRegions(provider?: RegionProvider) {
    return from(
      CloudConsoleClient.get<RegionItem[]>(apiMethodContext(arguments), {
        params: provider ? { provider } : {},
      }).then((value) => value.data ?? [])
    );
  }
}

const ApiRegionsService = new ApiRegionsServiceFactory();
export { ApiRegionsService };

const ApiService = new ApiServiceFactory();

export default ApiService;
